/**
 * Service pour la gestion des attributions d'utilisateurs aux sites
 */
import apiService from './api';

export interface UserSiteAssignment {
  id: number;
  user: number;
  site: number;
  role: number;
  session_type: 'PERMANENTE' | 'TEMPORAIRE';
  starts_at?: string;
  ends_at?: string;
  is_active: boolean;
  is_default: boolean;
  created_at: string;
  updated_at: string;
  created_by: number;
  // Champs calculés pour l'affichage
  site_name?: string;
  site_code?: string;
  role_label?: string;
  role_key?: string;
  user_username?: string;
  user_email?: string;
}

export interface CreateAssignmentRequest {
  user: number;
  site: number;
  role: number;
  session_type: 'PERMANENTE' | 'TEMPORAIRE';
  starts_at?: string;
  ends_at?: string;
  is_default?: boolean;
}

export interface UpdateAssignmentRequest {
  session_type?: 'PERMANENTE' | 'TEMPORAIRE';
  starts_at?: string;
  ends_at?: string;
  is_active?: boolean;
  is_default?: boolean;
}

class UserAssignmentService {
  /**
   * Service unifié pour la gestion des assignations utilisateur-site
   * Utilise les nouveaux endpoints unifiés du backend
   */

  /**
   * Construit l'URL de base pour les assignations d'un utilisateur
   */
  private getBaseUrl(userId: string): string {
    return `/admin/users/${userId}/assignments/`;
  }

  /**
   * Récupère toutes les assignations d'un utilisateur
   */
  async getAssignments(userId: string): Promise<UserSiteAssignment[]> {
    try {
      const response = await apiService.get(this.getBaseUrl(userId));
      // L'API retourne une structure paginée {count, next, previous, results}
      if (response.data && response.data.results) {
        return response.data.results;
      }
      return response.data || [];
    } catch (error: any) {
      console.error(`❌ Erreur lors de la récupération des assignations pour l'utilisateur ${userId}:`, error);
      throw error;
    }
  }

  /**
   * Crée une nouvelle assignation
   */
  async createAssignment(userId: string, data: CreateAssignmentRequest): Promise<UserSiteAssignment> {
    try {
      const response = await apiService.post(this.getBaseUrl(userId), data);
      return response.data;
    } catch (error: any) {
      console.error(`❌ Erreur lors de la création de l'assignation pour l'utilisateur ${userId}:`, error);
      throw error;
    }
  }

  /**
   * Met à jour une assignation existante
   */
  async updateAssignment(userId: string, assignmentId: string, data: UpdateAssignmentRequest): Promise<UserSiteAssignment> {
    try {
      const response = await apiService.put(`${this.getBaseUrl(userId)}${assignmentId}/`, data);
      return response.data;
    } catch (error: any) {
      console.error(`❌ Erreur lors de la mise à jour de l'assignation ${assignmentId}:`, error);
      throw error;
    }
  }

  /**
   * Supprime une assignation (désactive et supprime physiquement)
   */
  async deleteAssignment(userId: string, assignmentId: string): Promise<void> {
    try {
      await apiService.delete(`${this.getBaseUrl(userId)}${assignmentId}/`);
    } catch (error: any) {
      console.error(`❌ Erreur lors de la suppression de l'assignation ${assignmentId}:`, error);
      throw error;
    }
  }

  /**
   * Définit un site comme site par défaut pour un utilisateur
   */
  async setDefaultSite(userId: string, assignmentId: string): Promise<void> {
    try {
      await apiService.post(`${this.getBaseUrl(userId)}${assignmentId}/set-default/`);
    } catch (error: any) {
      console.error(`❌ Erreur lors de la définition du site par défaut pour l'assignation ${assignmentId}:`, error);
      throw error;
    }
  }

  /**
   * Active ou désactive une assignation
   */
  async activateAssignment(userId: string, assignmentId: string, isActive: boolean): Promise<void> {
    try {
      await apiService.post(`${this.getBaseUrl(userId)}${assignmentId}/activate/`, { is_active: isActive });
    } catch (error: any) {
      console.error(`❌ Erreur lors de l'activation/désactivation de l'assignation ${assignmentId}:`, error);
      throw error;
    }
  }

  /**
   * Récupère une assignation spécifique
   */
  async getAssignment(userId: string, assignmentId: string): Promise<UserSiteAssignment> {
    try {
      const response = await apiService.get(`${this.getBaseUrl(userId)}${assignmentId}/`);
      return response.data;
    } catch (error: any) {
      console.error(`❌ Erreur lors de la récupération de l'assignation ${assignmentId}:`, error);
      throw error;
    }
  }

  /**
   * Méthodes de compatibilité pour l'ancienne API (à supprimer progressivement)
   * @deprecated Utiliser les nouvelles méthodes unifiées
   */
  
  async assignUserToSite(userId: string, siteId: string, roleId: string, sessionType: string, isDefault: boolean = false): Promise<UserSiteAssignment> {
    console.warn('⚠️ assignUserToSite est déprécié, utiliser createAssignment');
    return this.createAssignment(userId, {
      user: parseInt(userId),
      site: parseInt(siteId),
      role: parseInt(roleId),
      session_type: sessionType as 'PERMANENTE' | 'TEMPORAIRE',
      is_default: isDefault
    });
  }

  async removeUserFromSite(userId: string, siteId: string): Promise<void> {
    console.warn('⚠️ removeUserFromSite est déprécié, utiliser deleteAssignment');
    // Récupérer d'abord l'assignation pour obtenir son ID
    const assignments = await this.getAssignments(userId);
    const assignment = assignments.find(a => a.site === parseInt(siteId));
    if (assignment) {
      await this.deleteAssignment(userId, assignment.id.toString());
    }
  }

  async setDefaultSiteForUser(userId: string, siteId: string): Promise<void> {
    console.warn('⚠️ setDefaultSiteForUser est déprécié, utiliser setDefaultSite');
    // Récupérer d'abord l'assignation pour obtenir son ID
    const assignments = await this.getAssignments(userId);
    const assignment = assignments.find(a => a.site === parseInt(siteId));
    if (assignment) {
      await this.setDefaultSite(userId, assignment.id.toString());
    }
  }
}

export const userAssignmentsService = new UserAssignmentService();
